export type PisteMapIssueContext = Readonly<{
  areaSlug: string;
  areaName: string | null;
  entrySlug: string | null;
  latitude: number | null;
  longitude: number | null;
  accuracyM: number | null;
  capturedAt: string;
}>;

function nullableString(value: unknown, maxLength: number): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, maxLength) : null;
}

function nullableCoordinate(
  value: unknown,
  maximumAbsolute: number,
): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (Math.abs(value) > maximumAbsolute) return null;
  return Math.round(value * 1_000_000) / 1_000_000;
}

export function parsePisteMapIssueContext(
  raw: unknown,
): PisteMapIssueContext | null {
  let value = raw;
  if (typeof raw === "string") {
    if (raw.length > 2_000) return null;
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const input = value as Record<string, unknown>;
  const areaSlug = nullableString(input.areaSlug, 100);
  const capturedAt = nullableString(input.capturedAt, 40);
  if (
    !areaSlug ||
    !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(areaSlug) ||
    !capturedAt ||
    !Number.isFinite(Date.parse(capturedAt))
  ) {
    return null;
  }
  const accuracyM =
    typeof input.accuracyM === "number" &&
    Number.isFinite(input.accuracyM) &&
    input.accuracyM >= 0 &&
    input.accuracyM <= 10_000
      ? Math.round(input.accuracyM)
      : null;
  return {
    areaSlug,
    areaName: nullableString(input.areaName, 120),
    entrySlug: nullableString(input.entrySlug, 100),
    latitude: nullableCoordinate(input.latitude, 90),
    longitude: nullableCoordinate(input.longitude, 180),
    accuracyM,
    capturedAt: new Date(capturedAt).toISOString(),
  };
}

export function pisteMapIssueLocationLabel(
  context: PisteMapIssueContext,
): string {
  const place =
    context.areaName ??
    context.entrySlug ??
    context.areaSlug;
  if (context.latitude === null || context.longitude === null) {
    return `${place}, position unavailable`;
  }
  const accuracy =
    context.accuracyM === null ? "" : `, accuracy ±${context.accuracyM} m`;
  return `${place}, ${context.latitude.toFixed(6)}, ${context.longitude.toFixed(6)}${accuracy}`;
}
